import React, { useState } from 'react';
import { XIcon, FileTextIcon, CameraIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
export interface ReportData {
  id?: string;
  title: string;
  reportType: 'Standard Installation' | 'Nedbrud/Udskiftning' | 'Flytning/Nedtagning' | 'Projektering' | 'Forgæves kørsel';
  date: string;
  customerId?: string;
  taskId?: string;
  category: 'all' | 'Ladeboks' | 'Andet';
  notes: string;
  createdAt: string;
  // Detailed inspection fields
  meterNumber?: string;
  installationNumber?: string;
  cleanedUp?: 'yes' | 'no';
  ownerInformed?: 'yes' | 'no' | 'not-relevant';
  earthConnectionAccessibility?: 'yes' | 'no';
  barriersOrEnclosures?: 'yes' | 'no' | 'not-relevant';
  sufficientWorkingSpace?: 'yes' | 'no';
  cablesCorrectlyLaid?: 'yes' | 'no' | 'not-relevant';
  cablesCorrectlyTerminated?: 'yes' | 'no' | 'not-relevant';
  mechanicalProtection?: 'yes' | 'no' | 'not-relevant';
  circuitsLabeledWithAffiliation?: 'yes' | 'no' | 'not-relevant';
  circuitsLabeledWithMaxFuse?: 'yes' | 'no' | 'not-relevant';
  materialsInstalledPerInstructions?: 'yes' | 'no';
  protectionConductorContinuity?: string;
  insulationMeasurement?: string;
  voltageControl?: string;
  rcdTripTime?: string;
  earthElectrodeResistance?: string;
  // Photos
  photoMeterCabinet?: string;
  photoChargingBox3m?: string;
  photoPanelWithoutCover?: string;
  photoPanelWithCover?: string;
  photoCableRouting?: string;
}
interface AddReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (report: Omit<ReportData, 'id' | 'createdAt'>) => void;
  customers: Array<{
    id: string;
    name: string;
    customerId: string;
  }>;
  tasks: Array<{
    id: string;
    taskId: string;
    title: string;
    customerId: string;
  }>;
  editingReport?: ReportData | null;
}
export function AddReportModal({
  isOpen,
  onClose,
  onAdd,
  customers,
  tasks,
  editingReport
}: AddReportModalProps) {
  const [title, setTitle] = useState(editingReport?.title || '');
  const [reportType, setReportType] = useState<ReportData['reportType']>(editingReport?.reportType || 'Standard Installation');
  const [date, setDate] = useState(editingReport?.date || '');
  const [customerId, setCustomerId] = useState(editingReport?.customerId || '');
  const [taskId, setTaskId] = useState(editingReport?.taskId || '');
  const [category, setCategory] = useState<'all' | 'Ladeboks' | 'Andet'>(editingReport?.category || 'all');
  const [notes, setNotes] = useState(editingReport?.notes || '');
  // Detailed inspection fields
  const [meterNumber, setMeterNumber] = useState(editingReport?.meterNumber || '');
  const [installationNumber, setInstallationNumber] = useState(editingReport?.installationNumber || '');
  const [cleanedUp, setCleanedUp] = useState<'yes' | 'no'>(editingReport?.cleanedUp || 'yes');
  const [ownerInformed, setOwnerInformed] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.ownerInformed || 'yes');
  const [earthConnectionAccessibility, setEarthConnectionAccessibility] = useState<'yes' | 'no'>(editingReport?.earthConnectionAccessibility || 'yes');
  const [barriersOrEnclosures, setBarriersOrEnclosures] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.barriersOrEnclosures || 'yes');
  const [sufficientWorkingSpace, setSufficientWorkingSpace] = useState<'yes' | 'no'>(editingReport?.sufficientWorkingSpace || 'yes');
  const [cablesCorrectlyLaid, setCablesCorrectlyLaid] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.cablesCorrectlyLaid || 'yes');
  const [cablesCorrectlyTerminated, setCablesCorrectlyTerminated] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.cablesCorrectlyTerminated || 'yes');
  const [mechanicalProtection, setMechanicalProtection] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.mechanicalProtection || 'yes');
  const [circuitsLabeledWithAffiliation, setCircuitsLabeledWithAffiliation] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.circuitsLabeledWithAffiliation || 'yes');
  const [circuitsLabeledWithMaxFuse, setCircuitsLabeledWithMaxFuse] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.circuitsLabeledWithMaxFuse || 'yes');
  const [materialsInstalledPerInstructions, setMaterialsInstalledPerInstructions] = useState<'yes' | 'no'>(editingReport?.materialsInstalledPerInstructions || 'yes');
  const [protectionConductorContinuity, setProtectionConductorContinuity] = useState(editingReport?.protectionConductorContinuity || '');
  const [insulationMeasurement, setInsulationMeasurement] = useState(editingReport?.insulationMeasurement || '');
  const [voltageControl, setVoltageControl] = useState(editingReport?.voltageControl || '');
  const [rcdTripTime, setRcdTripTime] = useState(editingReport?.rcdTripTime || '');
  const [earthElectrodeResistance, setEarthElectrodeResistance] = useState(editingReport?.earthElectrodeResistance || '');
  // Photo states
  const [photoMeterCabinet, setPhotoMeterCabinet] = useState(editingReport?.photoMeterCabinet || '');
  const [photoChargingBox3m, setPhotoChargingBox3m] = useState(editingReport?.photoChargingBox3m || '');
  const [photoPanelWithoutCover, setPhotoPanelWithoutCover] = useState(editingReport?.photoPanelWithoutCover || '');
  const [photoPanelWithCover, setPhotoPanelWithCover] = useState(editingReport?.photoPanelWithCover || '');
  const [photoCableRouting, setPhotoCableRouting] = useState(editingReport?.photoCableRouting || '');
  // Filter tasks by selected customer
  const filteredTasks = customerId ? tasks.filter(task => task.customerId === customerId) : [];
  const handlePhotoUpload = (setter: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setter(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !date) return;
    onAdd({
      title,
      reportType,
      date,
      customerId: customerId || undefined,
      taskId: taskId || undefined,
      category,
      notes,
      // Detailed fields
      meterNumber: meterNumber || undefined,
      installationNumber: installationNumber || undefined,
      cleanedUp,
      ownerInformed,
      earthConnectionAccessibility,
      barriersOrEnclosures,
      sufficientWorkingSpace,
      cablesCorrectlyLaid,
      cablesCorrectlyTerminated,
      mechanicalProtection,
      circuitsLabeledWithAffiliation,
      circuitsLabeledWithMaxFuse,
      materialsInstalledPerInstructions,
      protectionConductorContinuity: protectionConductorContinuity || undefined,
      insulationMeasurement: insulationMeasurement || undefined,
      voltageControl: voltageControl || undefined,
      rcdTripTime: rcdTripTime || undefined,
      earthElectrodeResistance: earthElectrodeResistance || undefined,
      // Photos
      photoMeterCabinet: photoMeterCabinet || undefined,
      photoChargingBox3m: photoChargingBox3m || undefined,
      photoPanelWithoutCover: photoPanelWithoutCover || undefined,
      photoPanelWithCover: photoPanelWithCover || undefined,
      photoCableRouting: photoCableRouting || undefined
    });
    onClose();
  };
  const RadioGroup = ({
    label,
    value,
    onChange,
    options
  }: {
    label: string;
    value: string;
    onChange: (value: any) => void;
    options: Array<{
      value: string;
      label: string;
    }>;
  }) => <div data-id="element-222">
      <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-223">
        {label}
      </label>
      <div className="flex gap-4" data-id="element-224">
        {options.map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer" data-id="element-225">
            <input type="radio" value={option.value} checked={value === option.value} onChange={e => onChange(e.target.value)} className="w-4 h-4 text-teal-600 focus:ring-teal-500" data-id="element-226" />
            <span className="text-sm text-stone-700" data-id="element-227">{option.label}</span>
          </label>)}
      </div>
    </div>;
  return <AnimatePresence data-id="element-228">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-229" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-230">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6" data-id="element-231">
              <div className="flex items-center justify-between mb-6 sticky top-0 bg-white pb-4 border-b border-stone-200" data-id="element-232">
                <div className="flex items-center gap-3" data-id="element-233">
                  <div className="p-2 bg-teal-100 rounded-lg" data-id="element-234">
                    <FileTextIcon className="w-6 h-6 text-teal-700" data-id="element-235" />
                  </div>
                  <div data-id="element-236">
                    <h2 className="text-2xl font-semibold text-stone-900" data-id="element-237">
                      {editingReport ? 'Rediger Rapport' : 'Tilføj Rapport'}
                    </h2>
                    <p className="text-sm text-stone-500 mt-1" data-id="element-238">
                      Udfyld alle relevante felter for rapporten
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-239">
                  <XIcon className="w-6 h-6" data-id="element-240" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8" data-id="element-241">
                {/* Basic Information */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-242">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-243">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-244">
                      1
                    </span>
                    Grundlæggende information
                  </h3>

                  <div className="space-y-4" data-id="element-245">
                    <Input label="Rapport titel" value={title} onChange={e => setTitle(e.target.value)} placeholder="f.eks. Installation rapport - Januar 2024" required data-id="element-246" />

                    <div className="grid grid-cols-2 gap-4" data-id="element-247">
                      <Select label="Opgavetype" value={reportType} onChange={e => setReportType(e.target.value as ReportData['reportType'])} options={[{
                    value: 'Standard Installation',
                    label: 'Standard Installation'
                  }, {
                    value: 'Nedbrud/Udskiftning',
                    label: 'Nedbrud/Udskiftning'
                  }, {
                    value: 'Flytning/Nedtagning',
                    label: 'Flytning/Nedtagning'
                  }, {
                    value: 'Projektering',
                    label: 'Projektering'
                  }, {
                    value: 'Forgæves kørsel',
                    label: 'Forgæves kørsel'
                  }]} required data-id="element-248" />

                      <Select label="Kunde" value={customerId} onChange={e => {
                    setCustomerId(e.target.value);
                    setTaskId(''); // Reset task when customer changes
                  }} options={[{
                    value: '',
                    label: 'Vælg kunde'
                  }, ...customers.map(c => ({
                    value: c.id,
                    label: `${c.name} (${c.customerId})`
                  }))]} data-id="element-249" />
                    </div>

                    {/* NEW: Task selection - only shown if customer is selected */}
                    {customerId && filteredTasks.length > 0 && <Select label="Tilknyt opgave (valgfrit)" value={taskId} onChange={e => setTaskId(e.target.value)} options={[{
                  value: '',
                  label: 'Ingen opgave'
                }, ...filteredTasks.map(task => ({
                  value: task.id,
                  label: `${task.taskId} - ${task.title}`
                }))]} data-id="element-250" />}

                    <div className="grid grid-cols-2 gap-4" data-id="element-251">
                      <Input label="Målernummer" value={meterNumber} onChange={e => setMeterNumber(e.target.value)} placeholder="Indtast målernummer" data-id="element-252" />

                      <Input label="Installationsnummer" value={installationNumber} onChange={e => setInstallationNumber(e.target.value)} placeholder="Indtast installationsnummer" data-id="element-253" />
                    </div>

                    <Input label="Dato" type="date" value={date} onChange={e => setDate(e.target.value)} required data-id="element-254" />
                  </div>
                </div>

                {/* General Checks */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-255">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-256">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-257">
                      2
                    </span>
                    Generelle kontroller
                  </h3>

                  <div className="space-y-4" data-id="element-258">
                    <RadioGroup label="Er der rengjort og ryddet op efter arbejdet?" value={cleanedUp} onChange={setCleanedUp} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }]} data-id="element-259" />

                    <RadioGroup label="Er ejer/bruger informeret om funktion og betjening?" value={ownerInformed} onChange={setOwnerInformed} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-260" />

                    <RadioGroup label="Tilgængelighed af jordlederforbindelser" value={earthConnectionAccessibility} onChange={setEarthConnectionAccessibility} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }]} data-id="element-261" />

                    <RadioGroup label="Barrierer eller kapslinger" value={barriersOrEnclosures} onChange={setBarriersOrEnclosures} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-262" />

                    <RadioGroup label="Tilstrækkelig plads til at arbejde/adgang til materiel (tavle)" value={sufficientWorkingSpace} onChange={setSufficientWorkingSpace} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }]} data-id="element-263" />
                  </div>
                </div>

                {/* Cable Installation */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-264">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-265">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-266">
                      3
                    </span>
                    Kabelinstallation
                  </h3>

                  <div className="space-y-4" data-id="element-267">
                    <RadioGroup label="Kabler korrekt oplagt" value={cablesCorrectlyLaid} onChange={setCablesCorrectlyLaid} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-268" />

                    <RadioGroup label="Kabler korrekt afsluttet i kapslinger" value={cablesCorrectlyTerminated} onChange={setCablesCorrectlyTerminated} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-269" />

                    <RadioGroup label="Mekanisk beskyttelse af strømkredse/kabler (f.eks. hvor de kommer op fra jord, er skjult over loft, under gulv eller i vægge)" value={mechanicalProtection} onChange={setMechanicalProtection} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-270" />
                  </div>
                </div>

                {/* Labeling and Materials */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-271">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-272">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-273">
                      4
                    </span>
                    Mærkning og materialer
                  </h3>

                  <div className="space-y-4" data-id="element-274">
                    <RadioGroup label="Installationer og strømkredse mærket med tilhørsforhold" value={circuitsLabeledWithAffiliation} onChange={setCircuitsLabeledWithAffiliation} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-275" />

                    <RadioGroup label="Installationer og strømkredse mærket med max sikringsstørrelse" value={circuitsLabeledWithMaxFuse} onChange={setCircuitsLabeledWithMaxFuse} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-276" />

                    <RadioGroup label="Valgte materialer installeret efter anvisninger" value={materialsInstalledPerInstructions} onChange={setMaterialsInstalledPerInstructions} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }]} data-id="element-277" />
                  </div>
                </div>

                {/* Measurements */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-278">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-279">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-280">
                      5
                    </span>
                    Målinger
                  </h3>

                  <div className="space-y-4" data-id="element-281">
                    <Input label="Kontinuitet i beskyttelsesleder og/eller udligningsforbindelse" value={protectionConductorContinuity} onChange={e => setProtectionConductorContinuity(e.target.value)} placeholder="Indtast måleværdi" data-id="element-282" />

                    <Input label="Isolationsmåling af installationen (MΩ)" value={insulationMeasurement} onChange={e => setInsulationMeasurement(e.target.value)} placeholder="f.eks. 500 MΩ" data-id="element-283" />

                    <Input label="Kontrol af spændingen (V)" value={voltageControl} onChange={e => setVoltageControl(e.target.value)} placeholder="f.eks. 230V" data-id="element-284" />

                    <Input label="Kontrol af fejlstrømsafbryder, udkoblingstid i ms" value={rcdTripTime} onChange={e => setRcdTripTime(e.target.value)} placeholder="f.eks. 25ms" data-id="element-285" />

                    <Input label="Overgangsmodstand til jordelektrode (Ω)" value={earthElectrodeResistance} onChange={e => setEarthElectrodeResistance(e.target.value)} placeholder="f.eks. 10Ω" data-id="element-286" />
                  </div>
                </div>

                {/* Photo Documentation */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-287">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-288">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-289">
                      6
                    </span>
                    Fotodokumentation
                  </h3>

                  <div className="space-y-4" data-id="element-290">
                    {[{
                  label: 'Målerskab/Målerfelt (uden dæksel)',
                  state: photoMeterCabinet,
                  setter: setPhotoMeterCabinet
                }, {
                  label: 'Ladeboks 3 meter fra opsætning',
                  state: photoChargingBox3m,
                  setter: setPhotoChargingBox3m
                }, {
                  label: 'Tavle uden dæksel',
                  state: photoPanelWithoutCover,
                  setter: setPhotoPanelWithoutCover
                }, {
                  label: 'Tavle med dæksel',
                  state: photoPanelWithCover,
                  setter: setPhotoPanelWithCover
                }, {
                  label: 'Kabelføring (valgfrit)',
                  state: photoCableRouting,
                  setter: setPhotoCableRouting
                }].map((photo, idx) => <div key={idx} data-id="element-291">
                        <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-292">
                          Foto: {photo.label}
                        </label>
                        <div className="flex items-center gap-3" data-id="element-293">
                          <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-stone-300 rounded-lg hover:border-teal-500 transition-colors cursor-pointer bg-white" data-id="element-294">
                            <CameraIcon className="w-5 h-5 text-stone-500" data-id="element-295" />
                            <span className="text-sm text-stone-600" data-id="element-296">
                              {photo.state ? 'Billede uploadet' : 'Vælg billede'}
                            </span>
                            <input type="file" accept="image/*" onChange={handlePhotoUpload(photo.setter)} className="hidden" data-id="element-297" />
                          </label>
                          {photo.state && <img src={photo.state} alt="Preview" className="w-16 h-16 object-cover rounded-lg border border-stone-200" data-id="element-298" />}
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-299">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-300">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-301">
                      7
                    </span>
                    Yderligere noter
                  </h3>

                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Tilføj eventuelle yderligere noter eller kommentarer..." rows={4} className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-302" />
                </div>

                <div className="flex gap-3 pt-2 sticky bottom-0 bg-white pb-4 border-t border-stone-200" data-id="element-303">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-304">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-305">
                    {editingReport ? 'Gem ændringer' : 'Opret rapport'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}